
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {NewsProfileGetResponse} from './NewsProfileGetResponse';

/**
 * NewsProfileGetRequest struct
 */
class NewsProfileGetRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {UUID=} newsProfileId
   * @param {string=} newsProfileName
   * @constructor
   */
  constructor (parent = new ConfigRequest(), newsProfileId = null, newsProfileName = null) {
    super()
    super.copy(parent)
    this.NewsProfileId = newsProfileId
    this.NewsProfileName = newsProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!NewsProfileGetRequest}
   * @param {!NewsProfileGetRequest} other Other struct
   * @returns {!NewsProfileGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.NewsProfileId != null) {
      this.NewsProfileId = new UUID(other.NewsProfileId)
    } else {
      this.NewsProfileId = null
    }
    if (other.NewsProfileName != null) {
      this.NewsProfileName = other.NewsProfileName
    } else {
      this.NewsProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!NewsProfileGetRequest}
   * @returns {!NewsProfileGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new NewsProfileGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new NewsProfileGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!NewsProfileGetRequest}
   * @param {!NewsProfileGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof NewsProfileGetRequest)) {
      throw new TypeError('Instance of NewsProfileGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!NewsProfileGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      NewsProfileId: ((this.NewsProfileId != null) ? this.NewsProfileId.toString() : null),
      NewsProfileName: ((this.NewsProfileName != null) ? this.NewsProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return NewsProfileGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!NewsProfileGetRequest} other Object value
   * @returns {!NewsProfileGetRequest} Created struct
   */
  static fromObject (other) {
    return new NewsProfileGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!NewsProfileGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return NewsProfileGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!NewsProfileGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6395
  }
}

export { NewsProfileGetRequest };

/**
 * Fast Binary Encoding NewsProfileGetRequest field model
 */
class FieldModelNewsProfileGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.NewsProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.NewsProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewsProfileId.fbeOffset + this.NewsProfileId.fbeSize), buffer, this.NewsProfileId.fbeOffset + this.NewsProfileId.fbeSize)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.NewsProfileId.fbeSize + this.NewsProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.NewsProfileId.fbeExtra + this.NewsProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelNewsProfileGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6395
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewsProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileId.fbeSize

    if ((fbeCurrentSize + this.NewsProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewsProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!NewsProfileGetRequest} fbeValue Default value, defaults is new NewsProfileGetRequest()
   * @returns {!NewsProfileGetRequest} NewsProfileGetRequest value
   */
  get (fbeValue = new NewsProfileGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!NewsProfileGetRequest} fbeValue NewsProfileGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.NewsProfileId = this.NewsProfileId.get()
    } else {
      fbeValue.NewsProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileId.fbeSize

    if ((fbeCurrentSize + this.NewsProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.NewsProfileName = this.NewsProfileName.get()
    } else {
      fbeValue.NewsProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelNewsProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {!NewsProfileGetRequest} fbeValue NewsProfileGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelNewsProfileGetRequest}
   * @param {NewsProfileGetRequest} fbeValue NewsProfileGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.NewsProfileId.set(fbeValue.NewsProfileId)
    this.NewsProfileName.set(fbeValue.NewsProfileName)
  }
}

export { FieldModelNewsProfileGetRequest };

/**
 * Fast Binary Encoding NewsProfileGetRequest model
 */
class NewsProfileGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelNewsProfileGetRequest(this.buffer, 4)
  }

  /**
   * Get the NewsProfileGetRequest model
   * @this {!NewsProfileGetRequestModel}
   * @returns {!FieldModelNewsProfileGetRequest} model NewsProfileGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!NewsProfileGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!NewsProfileGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return NewsProfileGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!NewsProfileGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelNewsProfileGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!NewsProfileGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!NewsProfileGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!NewsProfileGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!NewsProfileGetRequestModel}
   * @param {!NewsProfileGetRequest} value NewsProfileGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!NewsProfileGetRequestModel}
   * @param {!NewsProfileGetRequest} value NewsProfileGetRequest value, defaults is new NewsProfileGetRequest()
   * @return {!object} Deserialized NewsProfileGetRequest value and its size
   */
  deserialize (value = new NewsProfileGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new NewsProfileGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new NewsProfileGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!NewsProfileGetRequestModel}
   * @param {!number} prev Previous NewsProfileGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { NewsProfileGetRequestModel };
NewsProfileGetRequest.__has_response = true;
NewsProfileGetRequest.__response_class = NewsProfileGetResponse;
